/**
 * The approve link Mucka sends on WhatsApp for an order asked for there, and
 * the way back to WhatsApp once it is approved. Directive-free and pure: the
 * tool (server), the native link handler and the pay session route all read it.
 *
 * The link is an app page (`src/app/supplier-orders/[id]/approve`) that opens
 * pay.mucka.ai for the person signed in to Mucka in that browser. It carries
 * no credential, so a forwarded one approves nothing for anybody else. It is
 * also a universal link: on a phone with the app, it opens the app, which
 * opens the pay sheet itself so the passkey is Face ID on the signed-in app
 * rather than a sign-in in Safari first (Tom, 4 Nov 2026).
 */

/** Where an order's card can be opened in a phone's browser: the Orders list. */
export const ORDERS_PATH = '/settings/money/supplier-ordering';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const APPROVE = new RegExp(`^/supplier-orders/(${UUID})/approve/?$`, 'i');

export function approvePath(orderId: string): string {
  return `/supplier-orders/${encodeURIComponent(orderId)}/approve`;
}

/**
 * Added to the pay sheet's fragment when it is opened from the WhatsApp link,
 * so its done page says Back to WhatsApp; from the card in Mucka it says Back
 * to Mucka.
 */
export const FROM_WHATSAPP = 'from=whatsapp';

/** The order an approve link is for, or null for any other path. */
export function approveLinkOrderId(pathname: string): string | null {
  return APPROVE.exec(pathname)?.[1] ?? null;
}

/**
 * Mucka's own WhatsApp chat, for "Back to WhatsApp": an order asked for there
 * is followed up there, so that is where they go back to. A wa.me link is a
 * universal link, so a phone opens WhatsApp on that chat. Null when the number
 * is not configured, and the caller says "Back to Mucka" as before.
 */
export function whatsAppChatUrl(display: string | undefined = process.env.NEXT_PUBLIC_WHATSAPP_BUSINESS_DISPLAY): string | null {
  const digits = (display ?? '').replace(/\D/g, '');
  return digits.length >= 10 ? `https://wa.me/${digits}` : null;
}
